import React from 'react';
import { logger } from '@/lib/logger';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Brain, AlertCircle } from 'lucide-react';
import { SkeletonCoaching } from '@/components/ui/loading/SkeletonCoaching';
import { useRunSuggestion } from '@/hooks/useRunSuggestion';
import { useWeatherData } from '@/hooks/useWeatherData';
import { RunSuggestionCard } from './components/RunSuggestionCard';
import { RunSuggestionInsights } from './components/RunSuggestionInsights';
import type { TimeRange } from '@/lib/utils/dateRange';
import { StatusBadge } from '@/components/ai-coach/common/StatusBadge';

interface AIRunSuggestionProps {
  timeRange: TimeRange;
}

export const AIRunSuggestion: React.FC<AIRunSuggestionProps> = ({ timeRange }) => {
  const { suggestionData, isLoading, error, trainingLoadData } = useRunSuggestion(timeRange);
  const { weatherData } = useWeatherData();


  if (isLoading) {
    return <SkeletonCoaching />;
  }

  if (!suggestionData?.suggestion) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-purple-500" />
            AI Run Coach
          </CardTitle>
          <CardDescription>Get personalized run recommendations</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6">
            {error ? (
              <>
                <AlertCircle className="h-12 w-12 mx-auto mb-3 text-red-400" />
                <p className="text-red-600 font-medium mb-2">Analysis Error</p>
                <p className="text-gray-500 text-sm mb-4">
                  {error.message || 'Unable to load AI insights'}
                </p>
                <div className="flex gap-2 justify-center">
                  <Button onClick={() => window.location.reload()} variant="outline" size="sm">
                    Try Again
                  </Button>
                  <Button 
                    onClick={() => logger.debug('Debug info:', { error, trainingLoadData }, 'AIRunSuggestion')} 
                    variant="ghost" 
                    size="sm"
                  >
                    Debug Info
                  </Button>
                </div>
              </>
            ) : (
              <>
                <AlertCircle className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                <p className="text-gray-500 mb-4">Need more running data to generate suggestions</p>
                <Button onClick={() => window.location.reload()} variant="outline" size="sm">
                  Try Again
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  const { suggestion, reasoning, coaching_tips, alternatives, focus_areas } = suggestionData;

  return (
    <Card className="border-l-4 border-l-purple-500">
      <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Brain className="h-5 w-5 text-purple-500" />
                <CardTitle>AI Run Coach</CardTitle>
                <StatusBadge kind={suggestionData?.source === 'ai' ? 'modeAi' : 'modeStandard'} />
                {error && (
                  <StatusBadge kind="limited" />
                )}
              </div>
              <div className="flex gap-1">
                <Button onClick={() => window.location.reload()} variant="ghost" size="sm">
                  Refresh
                </Button>
              </div>
            </div>
        <CardDescription>Your personalized next run recommendation</CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Main Suggestion */}
        <RunSuggestionCard suggestion={suggestion} weatherData={weatherData} />

        {/* Insights and Tips */}
        <RunSuggestionInsights 
          reasoning={reasoning}
          coaching_tips={coaching_tips}
          focus_areas={focus_areas}
          alternatives={alternatives}
        />
      </CardContent>
    </Card>
  );
};